import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useMemo } from "react";

const TOP_TRENDS_API_ENDPOINT: keyof paths = '/TopTrends';
export type TopTrendsRequest = components['schemas']['TopTrendFilterInput'];
export type TopTrendsResponse = components['schemas']['TopTrendResultOutput'];

type TopTrendsDataItemRaw = NonNullable<NonNullable<TopTrendsResponse>['data']>[number];

export type TopTrendsDataItem = {
    word: TopTrendsDataItemRaw['word'];
    penetrationValue: number | null;
    incidenceValue: number | null;
    restaurantCount: number | null;
    growthValue: number | null;
    cagrValue: number | null;
    knowItValue: number | null;
    haveTriedItValue: number | null;
    loveOrLikeItValue: number | null;
};

export function getTopTrendsCopyTableData(baseData: string[][], growthPeriod: number): string[][] {
    if (baseData.length === 0) return baseData;

    const formattedDate = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const yearPrefix = `${growthPeriod} YEAR${growthPeriod > 1 ? 'S' : ''}`;

    const headerReplacements: Record<string, string> = {
        'PAST GROWTH': `${yearPrefix} PAST GROWTH`,
        'CAGR': `${yearPrefix} CAGR`,
        'GROWTH PERIOD': '',
        'CONSUMERS': '',
    };

    const headerRowCount = baseData.length > 1 && baseData[0].some(c => c === 'GROWTH PERIOD' || c === 'CONSUMERS' || c === '') ? 2 : 1;

    const transformedHeaders = baseData.slice(0, headerRowCount)
        .map(row =>
            row.map(cell => headerReplacements[cell] ?? cell)
        )
        .filter(row => row.some(cell => cell !== ''));

    const dataRows = baseData.slice(headerRowCount);

    return [
        [`Extracted ${formattedDate}`],
        ['Questions? Call us at (312) 655-0622 or email support@datassential.com'],
        [],
        ...transformedHeaders,
        ...dataRows,
    ];
}

function isNumericValue(value: any): boolean {
    return typeof value === 'number' && !isNaN(value);
}

export default function useTopTrendsData() {
    const {
        data: rawData,
        isLoading,
        error,
        doFetch,
        cancel,
    } = useAxios<TopTrendsResponse, TopTrendsRequest>({
        method: 'post',
        url: TOP_TRENDS_API_ENDPOINT,
        lazy: true,
    });

    const fetchTopTrends = useCallback(async (requestData: TopTrendsRequest) => {
        await doFetch({ data: requestData });
    }, [doFetch]);

    const transformedData = useMemo((): TopTrendsDataItem[] => {
        if (!rawData?.data) return [];

        const flavorDataArray = rawData.flavorData?.data ?? [];

        return rawData.data.map((item, index) => {
            const flavorItem = flavorDataArray[index];
            return {
                word: item.word,
                penetrationValue: isNumericValue(item.penetrationValue) ? (item.penetrationValue ?? null) : null,
                incidenceValue: isNumericValue(item.incidenceValue) ? (item.incidenceValue ?? null) : null,
                restaurantCount: isNumericValue(item.restaurantCount) ? (item.restaurantCount ?? null) : null,
                growthValue: isNumericValue(item.growthValue) ? (item.growthValue ?? null) : null,
                cagrValue: isNumericValue(item.cagrValue) ? (item.cagrValue ?? null) : null,
                knowItValue: isNumericValue(flavorItem?.knowIt) ? (flavorItem?.knowIt ?? null) : null,
                haveTriedItValue: isNumericValue(flavorItem?.haveTriedIt) ? (flavorItem?.haveTriedIt ?? null) : null,
                loveOrLikeItValue: isNumericValue(flavorItem?.loveOrLikeIt) ? (flavorItem?.loveOrLikeIt ?? null) : null,
            };
        });
    }, [rawData]);

    const data = useMemo(() => {
        if (!rawData) return null;
        return {
            ...rawData,
            data: transformedData,
        };
    }, [rawData, transformedData]);

    return {
        data,
        isLoading,
        error,
        doFetch: fetchTopTrends,
        cancel,
    };
}
